import {useCallback, useEffect, useRef, useState} from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    FormGroup,
    InputAdornment,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import CloudOffOutlinedIcon from "@mui/icons-material/CloudOffOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import {alpha} from "@mui/material/styles";
import {useQueryClient} from "@tanstack/react-query";
import {useTranslation} from "react-i18next";
import {
    chatHistoryDetailKey,
    chatHistoryListKey,
    useChatIndex,
    useEditChatMessage,
} from "../../api/chat/chatQueries";
import {Chat, ChatDocument, ChatDocumentStatus, Message, PendingChatUpload} from "../../util/types";
import {MessageRole} from "../../util/role";
import ArenaSnackBar from "../ArenaSnackBar";
import AttachmentMenuButton from "./AttachmentMenuButton";
import MessageList from "./MessageList";
import {AttachmentNotice} from "./useChatAttachments";

type Props = {
    attachmentNotice: AttachmentNotice | undefined;
    chat: Chat | undefined;
    chatHistoryError: string | undefined;
    chatId: string | undefined;
    clearAttachmentNotice: () => void;
    isChatHistoryLoading: boolean;
    isChatProcessing: boolean;
    onChatCreated: (chatId: string) => void;
    onFilesSelected: (selectedFiles: FileList | null) => void;
    onRemoveDocument: (documentId: string) => void;
    pendingUploads: PendingChatUpload[];
    scrollContainerRef: React.RefObject<HTMLDivElement | null>;
};

const getDocumentTooltip = (document: ChatDocument, translate: (key: string) => string): string | undefined => {
    switch (document.status) {
        case "EXPIRED":
            return translate("chat.attachments.tooltips.expired");
        case "MISSING":
            return translate("chat.attachments.tooltips.missing");
        default:
            return undefined;
    }
};

const renderDocumentIcon = (status: ChatDocumentStatus): React.ReactElement | undefined => {
    switch (status) {
        case "EXPIRED":
            return <ErrorOutlineOutlinedIcon fontSize="small"/>;
        case "MISSING":
            return <CloudOffOutlinedIcon fontSize="small"/>;
        default:
            return undefined;
    }
};

const ChatBox: React.FC<Props> = ({
                                      attachmentNotice,
                                      chat,
                                      chatHistoryError,
                                      chatId,
                                      clearAttachmentNotice,
                                      isChatHistoryLoading,
                                      isChatProcessing,
                                      onChatCreated,
                                      onFilesSelected,
                                      onRemoveDocument,
                                      pendingUploads,
                                      scrollContainerRef,
                                  }) => {
    const [input, setInput] = useState("");
    const [editingMessageIndex, setEditingMessageIndex] = useState<number | null>(null);
    const [editingValue, setEditingValue] = useState("");
    const [messages, setMessages] = useState<Message[]>([]);
    const [submitError, setSubmitError] = useState<string | undefined>(undefined);
    const isMountedRef = useRef(true);

    const queryClient = useQueryClient();
    const chatIndexMutation = useChatIndex();
    const editChatMessageMutation = useEditChatMessage();
    const {t: translate} = useTranslation();

    const isSubmitting = chatIndexMutation.isPending;
    const isEditPending = editChatMessageMutation.isPending;
    const documents = chat?.documents ?? [];
    const hasUploadingDocuments = pendingUploads.length > 0;
    const hasUnavailableDocuments = documents.some((document) => document.status === "EXPIRED" || document.status === "MISSING");
    const isComposerDisabled = isChatProcessing || isChatHistoryLoading || isEditPending;
    const isSubmitDisabled = isComposerDisabled || hasUploadingDocuments || hasUnavailableDocuments;
    const chatErrorMessage = chatHistoryError || submitError;
    const isNewChatPlaceholderVisible = !chatId && messages.length === 0;

    let documentStateNotice = undefined;
    if (hasUploadingDocuments) {
        documentStateNotice = translate("chat.attachments.status.uploading");
    }
    if (hasUnavailableDocuments) {
        documentStateNotice = translate("chat.attachments.status.unavailable");
    }

    const scrollMessagesToBottom = useCallback(() => {
        const animationFrameId = requestAnimationFrame(() => {
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
            }
        });

        return () => cancelAnimationFrame(animationFrameId);
    }, [scrollContainerRef]);

    const handleSubmit = async(event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (isSubmitDisabled || input.trim() === "") {
            return;
        }

        setSubmitError(undefined);

        const submittedInput = input;
        const previousMessages = messages;
        const inputMessage: Message = {
            content: submittedInput,
            chatIndex: messages.length,
            role: MessageRole.USER,
            timestamp: Date.now(),
        };
        const updatedMessages = [...messages, inputMessage];
        setMessages(updatedMessages);
        setInput("");

        try {
            const updatedChat: Chat = {
                chatId,
                messages: updatedMessages,
                documents,
            };
            const result = await chatIndexMutation.mutateAsync({
                chat: updatedChat,
            });
            const responseMessage: Message = {
                content: result.message.content,
                chatIndex: result.message.chatIndex,
                role: result.message.role as MessageRole,
                timestamp: result.message.timestamp,
            };
            const nextChat: Chat = {
                chatId: result.chatId,
                messages: [...updatedMessages, responseMessage],
                documents,
            };

            queryClient.setQueryData(chatHistoryDetailKey(result.chatId), nextChat);
            void queryClient.invalidateQueries({queryKey: chatHistoryListKey});

            if (!isMountedRef.current) {
                return;
            }

            if (!chatId) {
                onChatCreated(result.chatId);
                return;
            }

            setMessages(nextChat.messages);
        } catch (error) {
            if (isMountedRef.current) {
                setMessages(previousMessages);
                setInput(submittedInput);
                setSubmitError(error instanceof Error ? error.message : translate("errors.TECHNICAL_ERROR"));
            }
        }
    };

    const handleInputKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (!(event.target instanceof HTMLTextAreaElement)) {
            return;
        }

        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            event.target.form?.requestSubmit();
        }
    };

    const handleEditStart = useCallback((message: Message) => {
        setSubmitError(undefined);
        setEditingMessageIndex(message.chatIndex);
        setEditingValue(message.content);
    }, []);

    const handleEditCancel = useCallback(() => {
        if (isEditPending) {
            return;
        }

        setEditingMessageIndex(null);
        setEditingValue("");
    }, [isEditPending]);

    const handleEditSave = useCallback(async() => {
        if (!chatId || editingMessageIndex === null || editingValue.trim() === "") {
            return;
        }

        const originalMessage = messages.find((message) => message.chatIndex === editingMessageIndex);
        if (!originalMessage) {
            return;
        }

        if (editingValue === originalMessage.content) {
            setEditingMessageIndex(null);
            setEditingValue("");
            return;
        }

        setSubmitError(undefined);

        try {
            const editedChat = await editChatMessageMutation.mutateAsync({
                chatId,
                messageIndex: editingMessageIndex,
                message: {
                    ...originalMessage,
                    content: editingValue,
                },
            });

            setMessages(editedChat.messages);
            setEditingMessageIndex(null);
            setEditingValue("");

            if (editedChat.chatId) {
                onChatCreated(editedChat.chatId);
            }
        } catch (error) {
            if (isMountedRef.current) {
                setSubmitError(error instanceof Error ? error.message : translate("errors.TECHNICAL_ERROR"));
            }
        }
    }, [chatId, editChatMessageMutation, editingMessageIndex, editingValue, messages, onChatCreated, translate]);

    useEffect(() => {
        isMountedRef.current = true;

        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (!chatId) {
            setEditingMessageIndex(null);
            setEditingValue("");
            setMessages([]);
            setSubmitError(undefined);
            return;
        }

        if (chat) {
            setEditingMessageIndex(null);
            setEditingValue("");
            setMessages(chat.messages);
            setSubmitError(undefined);
        }
    }, [chat, chatId]);

    useEffect(() => {
        return scrollMessagesToBottom();
    }, [messages.length, scrollMessagesToBottom]);

    return (
        <Box
            sx={{
                width: "min(100%, max(60%, 600px))",
                display: "flex",
                flexDirection: "column",
                minHeight: "100%",
                minWidth: 0,
                pt: 8,
            }}
        >
            <Box
                component="ul"
                sx={{
                    listStyleType: "none",
                    padding: 0,
                    margin: 0,
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {!chatId && messages.length === 0 ? (
                    <Box
                        component="li"
                        sx={{
                            listStyle: "none",
                            flexGrow: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            color: "text.secondary",
                            px: 2,
                        }}
                    >
                        <Typography variant="h6">{translate("chat.noChatSelected")}</Typography>
                    </Box>
                ) : (
                    <MessageList
                        messages={messages}
                        editingMessageIndex={editingMessageIndex}
                        editingValue={editingValue}
                        isEditButtonDisabled={isSubmitting}
                        isEditPending={isEditPending}
                        onEditCancel={handleEditCancel}
                        onEditChange={setEditingValue}
                        onEditSave={() => void handleEditSave()}
                        onEditStart={handleEditStart}
                    />
                )}
            </Box>
            <Box
                sx={(theme) => ({
                    position: "sticky",
                    bottom: 0,
                    zIndex: 1,
                    width: "100%",
                    alignSelf: "stretch",
                    mt: 2,
                    pt: 2,
                    pb: 2,
                    backgroundColor: alpha(theme.palette.background.default, 0.94),
                    backdropFilter: "blur(10px)",
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: -24,
                        left: 0,
                        right: 0,
                        height: 24,
                        background: `linear-gradient(180deg, ${alpha(theme.palette.background.default, 0)} 0%, ${alpha(
                            theme.palette.background.default,
                            0.94,
                        )} 100%)`,
                        pointerEvents: "none",
                    },
                })}
            >
                {attachmentNotice?.severity === "error" ? (
                    <Alert onClose={clearAttachmentNotice} severity="error" sx={{mb: 1}}>
                        {attachmentNotice.message}
                    </Alert>
                ) : null}
                {chatErrorMessage ? (
                    <Alert severity="error" sx={{mb: 1}}>
                        {chatErrorMessage}
                    </Alert>
                ) : null}
                {documentStateNotice ? (
                    <Typography variant="body2" sx={{mb: 1}}>
                        {documentStateNotice}
                    </Typography>
                ) : null}
                {isChatHistoryLoading || isSubmitting ? (
                    <Typography variant="body2" sx={{mb: 1}}>
                        {translate("chat.loading")}
                    </Typography>
                ) : null}
                <Box component="form" onSubmit={handleSubmit} sx={{display: "flex", flexDirection: "column", gap: 1}}>
                    <Box
                        sx={{
                            minHeight: isNewChatPlaceholderVisible ? 40 : 0,
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            alignContent: "flex-start",
                        }}
                    >
                        {documents.map((document) => {
                            const tooltip = getDocumentTooltip(document, translate);
                            const chip = (
                                <Chip
                                    icon={renderDocumentIcon(document.status)}
                                    key={document.documentId}
                                    label={document.filename}
                                    onDelete={() => onRemoveDocument(document.documentId)}
                                    sx={{maxWidth: "100%"}}
                                    variant="outlined"
                                />
                            );

                            return tooltip ? (
                                <Tooltip key={document.documentId} title={tooltip}>
                                    <span>{chip}</span>
                                </Tooltip>
                            ) : (
                                chip
                            );
                        })}
                        {pendingUploads.map((attachment) => (
                            <Tooltip key={attachment.id} title={translate("chat.attachments.tooltips.uploading")}>
                                <span>
                                    <Chip
                                        icon={<CircularProgress size={16} />}
                                        label={attachment.filename}
                                        sx={{maxWidth: "100%"}}
                                        variant="outlined"
                                    />
                                </span>
                            </Tooltip>
                        ))}
                    </Box>
                    <Typography color="text.secondary" variant="caption">
                        {translate("chat.attachments.helperText", {
                            maxFiles: 3,
                            maxSizeMb: 10,
                        })}
                    </Typography>
                    <FormGroup
                        style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            gap: "10px",
                            width: "100%",
                        }}
                    >
                        <TextField
                            multiline
                            minRows={1}
                            maxRows={5}
                            value={input}
                            disabled={isComposerDisabled}
                            onChange={(event) => setInput(event.target.value)}
                            onKeyDown={handleInputKeyDown}
                            placeholder={translate("chat.inputPlaceholder")}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start" sx={{mr: 0.5}}>
                                            <AttachmentMenuButton
                                                accept=".pdf,.txt,.doc,.docx,.md,.pptx,application/pdf,text/plain,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/markdown,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                                                disabled={isComposerDisabled}
                                                onFilesSelected={onFilesSelected}
                                                openMenuLabel={translate("chat.attachments.openMenu")}
                                                uploadFileLabel={translate("chat.attachments.uploadFile")}
                                            />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                            sx={{
                                flex: 1,
                            }}
                        />
                        <Tooltip title={translate("chat.send")}>
                            <Button
                                type="submit"
                                disabled={isSubmitDisabled}
                                aria-label={translate("chat.send")}
                                variant="contained"
                                sx={(theme) => ({
                                    minWidth: 52,
                                    minHeight: 52,
                                    borderRadius: 3,
                                    px: 0,
                                    color: theme.palette.primary.contrastText,
                                    bgcolor: theme.palette.primary.main,
                                    border: "1px solid",
                                    borderColor: alpha(theme.palette.primary.dark, 0.28),
                                    boxShadow: `0 10px 22px ${alpha(theme.palette.primary.main, 0.18)}`,
                                    "&:hover": {
                                        bgcolor: theme.palette.primary.dark,
                                        boxShadow: `0 14px 28px ${alpha(theme.palette.primary.main, 0.24)}`,
                                    },
                                })}
                            >
                                <SendIcon />
                            </Button>
                        </Tooltip>
                    </FormGroup>
                </Box>
            </Box>
            <ArenaSnackBar
                message={attachmentNotice?.severity === "info" ? attachmentNotice.message : undefined}
                onClose={clearAttachmentNotice}
                severity="info"
                autoHideDuration={6000}
            />
        </Box>
    );
};

export default ChatBox;
